import React, { useState, useEffect } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useProjectId } from "../context/ProjectIdContext";
import { Toast, ToastDescription, ToastTitle, ToastViewport } from "./ui/toast";
import { Input } from "./ui/input";
import { useRef } from "react";
import LoadingButton from "./LoadingButton";

const API_URL = process.env.REACT_APP_API_URL; // Ensure this matches your backend URL
const Checkout = () => {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  const invoiceRef = useRef(); // Create ref for invoice
  const [toastList, setToastList] = useState([]);
  const { projectId } = useProjectId();
  const { cartItems, removeFromCart } = useCart();

  const [couponCode, setCouponCode] = useState("");
  const [appliedCouponCode, setAppliedCouponCode] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [isApplying, setIsApplying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Assign totalCost from the first item's estimatedTotalCost (or default to 0)
  const totalCost =
    cartItems.length > 0 ? cartItems[0].items[0].estimatedTotalCost : 0;

  const [discountedTotal, setDiscountedTotal] = useState(totalCost);

  const [projectDetails, setProjectDetails] = useState({
    title: "",
    location: "",
    clientName: "",
    sippInstaller: "",
    sippCalcDesignDate: "",
  });

  // Fetching Project details
  useEffect(() => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const credentials = `${username}:${password}`;
    const encodedCredentials = btoa(credentials); // Base64 encode the credentials

    const fetchProjectDetails = async () => {
      if (!projectId) {
        console.error("No project ID provided.");
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/project/${projectId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          },
        });

        if (response.ok) {
          const result = await response.json();
          const project = result.data.data.project;

          // console.log("Project details fetched:", project);

          // Update state with fetched project details
          setProjectDetails({
            title: project.title,
            location: project.location,
            clientName: project.clientName,
            sippInstaller: project.sippInstaller,
            sippCalcDesignDate: project.sippCalcDesignDate,
          });
        } else {
          console.error(
            "Failed to fetch project details:",
            response.statusText,
          );
        }
      } catch (error) {
        console.error("Error fetching project details:", error);
      }
    };

    fetchProjectDetails();
  }, [projectId]); // Only runs when the `projectId` changes

  // const handleChange = (e) => {
  //   setFormData({ ...formData, [e.target.id]: e.target.value });
  // };

  // Handle coupon input change
  const handleCouponChange = (e) => {
    setCouponCode(e.target.value);
  };

  // Handle applying coupon
  const handleApplyCoupon = async () => {
    if (!couponCode) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "Error",
          description: "Enter a coupon code!",
          variant: "destructive",
        },
      ]);
      return;
    }

    setIsApplying(true);
    try {
      const token = localStorage.getItem("x-auth-token");
      const response = await fetch(`${API_URL}/api/coupon/${couponCode}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          "x-auth-token": token,
        },
      });

      const responseData = await response.json();

      if (response.ok) {
        const discountAmount = responseData.data.summary.coupon.discountAmount;
        setDiscount(discountAmount);
        setAppliedCouponCode(couponCode); // Save applied coupon code
        // Calculate new discounted total
        const newTotal = totalCost - discountAmount;
        setDiscountedTotal(newTotal);

        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "Success",
            description: `Coupon applied! Discount: $${discountAmount}`,
            variant: "default",
          },
        ]);
      } else {
        throw new Error(responseData.data.message || "Invalid coupon code.");
      }
    } catch (error) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "Error",
          description: error.message,
          variant: "destructive",
        },
      ]);
    } finally {
      setIsApplying(false);
    }
  };

  // For Sending emails with Pdf
  const handlePlaceOrder = async () => {
    // alert("handlePlaceOrder clicked!");

    setIsLoading(true);

    const invoiceElement = invoiceRef.current;

    if (!invoiceElement) {
      console.error("Invoice container not found.");
      return;
    }

    // // Make invoice visible before capturing
    // invoiceElement.classList.remove("tw-hidden");
    // invoiceElement.style.display = "block";
    // invoiceElement.style.opacity = "1";
    // invoiceElement.style.visibility = "visible";

    await new Promise((resolve) => setTimeout(resolve, 500)); // Wait before capturing

    try {
      // Capture the invoice as an image
      const canvas = await html2canvas(invoiceElement, {
        scale: 2, // High resolution
        useCORS: true, // Fixes issues with external images
        width: 794, // A4 width in px (at 96 DPI)
        height: 1123, // A4 height in px (at 96 DPI)
        windowWidth: 794,
        windowHeight: 1123,
        allowTaint: true,
        logging: true,
      });

      // // Make invoice invisible before capturing
      // invoiceElement.classList.add("tw-hidden");
      // invoiceElement.style.display = "none";

      const imgData = canvas.toDataURL("image/jpeg", 0.8); // 80% quality JPEG
      const imgPreview = document.createElement("img");
      imgPreview.src = imgData;
      document.body.appendChild(imgPreview); // Append image to check output

      // // Show image preview
      // const imgPreviewWindow = window.open();
      // imgPreviewWindow.document.write(
      //   `<html><body><h3>Invoice Image Preview</h3><img src="${imgData}" style="max-width:100%"/></body></html>`
      // );
      // imgPreviewWindow.document.close();

      // Generate PDF
      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 10, imgWidth, imgHeight);
      const pdfBlob = pdf.output("blob", { compression: "SLOW" });

      // // Show PDF preview
      // const pdfURL = URL.createObjectURL(pdfBlob);
      // window.open(pdfURL, "_blank");

      const formData = new FormData();

      // Append PDF file
      formData.append("invoice", pdfBlob, "invoice.pdf");

      // Append required fields
      formData.append("couponCode", appliedCouponCode);
      formData.append("totalPrice", totalCost); // Make sure totalCost is defined

      // console.log(formData)

      // console.log("PDF Blob:", pdfBlob);
      // console.log("PDF Blob Size:", pdfBlob.size); // Should not be 0

      const token = localStorage.getItem("x-auth-token");
      const response = await fetch(`${API_URL}/api/upload-invoice`, {
        method: "POST",
        headers: {
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          "x-auth-token": token,
        },
        body: formData, // Send as multipart/form-data
      });

      console.log("Response received:", response);

      if (response.ok) {
        setToastList([
          ...toastList,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description: "Invoice PDF sent successfully!",
            variant: "default",
          },
        ]);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      setToastList([
        ...toastList,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Failed to generate PDF.",
          variant: "destructive",
        },
      ]);
      setIsLoading(false);
    }
  };

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-px-4">
      {/* Buttons */}
      {/* <div className="tw-my-4 tw-flex tw-gap-4">
        <Button
          onClick={() => handlePlaceOrder(true)}
          className="tw-bg-[#00B5ED] tw-text-white"
        >
          Preview PDF
        </Button>
        <Button
          onClick={() => handlePlaceOrder(false)}
          className="tw-bg-[#00B5ED] tw-text-white"
        >
          Send Invoice
        </Button>
      </div> */}

      <Card className="tw-w-full tw-shadow-sm">
        <CardHeader className="tw-flex tw-justify-between">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
            <div>
              <CardTitle className="tw-text-xl md:tw-text-3xl h-sm:tw-text-2xl">
                Checkout
              </CardTitle>
              <CardDescription className="tw-text-[#828282]">
                Complete your purchase by providing your details below.
              </CardDescription>
            </div>

            {/* Coupon Code Input */}
            <div className="tw-flex tw-items-center tw-gap-2 tw-mt-4 md:tw-mt-0">
              <Input
                type="text"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={handleCouponChange}
                className="tw-border tw-border-[#00B5ED] tw-rounded-lg tw-bg-[#f2f2f2] tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#00B5ED]"
              />
              <Button
                onClick={handleApplyCoupon}
                disabled={isApplying}
                className="tw-bg-[#00B5ED] tw-text-white tw-px-4 tw-py-2 tw-rounded-lg hover:tw-bg-[#009ACD]"
              >
                {isApplying ? "Applying..." : "Apply"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Display Applied Discount */}
          {/* {discount > 0 && (
            <div className="tw-mb-4 tw-text-green-600 tw-font-semibold">
              Coupon Applied! Discount: ${discount}
            </div>
          )} */}

          {/* Display Original and Discounted Total */}
          {discount > 0 && (
            <div className="tw-mt-2 tw-p-4 tw-bg-[#E6F7FF] tw-border-l-4 tw-border-[#00B5ED] tw-rounded-lg">
              <p className="tw-text-md tw-font-bold">
                Total Cost:{" "}
                <span className="tw-line-through tw-text-gray-500">
                  ${parseFloat(totalCost).toLocaleString()}
                </span>
              </p>
              <p className="tw-font-bold">
                Discounted Total:{" "}
                <span className="tw-text-green-600">
                  ${parseFloat(discountedTotal).toLocaleString()}
                </span>
              </p>
            </div>
          )}

          {/* Change to Information from backend */}
          <div className="tw-my-6">
            <ul>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Project Title:
                </span>{" "}
                {projectDetails.title}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Project Location:
                </span>{" "}
                {projectDetails.location}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Client:
                </span>{" "}
                {projectDetails.clientName}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  SIPP Installer:
                </span>{" "}
                {projectDetails.sippInstaller}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  SIPP Calc Design Date:
                </span>{" "}
                {projectDetails.sippCalcDesignDate}
              </li>
            </ul>
          </div>

          <div>
            {cartItems.length === 0 ? (
              <p className="tw-text-center tw-text-gray-500">
                Your cart is empty.
              </p>
            ) : (
              <>
                <div className="tw-text-right tw-justify-end tw-mt-4">
                  <button
                    onClick={() =>
                      cartItems.forEach((cart) => removeFromCart(cart.id))
                    }
                    className="tw-text-white tw-bg-red-500 tw-p-2 tw-rounded-full tw-shadow hover:tw-bg-red-600"
                  >
                    <Trash2 className="tw-h-6 tw-w-6" />
                  </button>
                </div>
                <div className="tw-overflow-x-auto">
                  <table className="tw-w-full tw-mt-6 tw-border-collapse tw-border tw-text-left tw-bg-white">
                    <thead>
                      <tr className="tw-bg-[#00B5ED] tw-text-white">
                        <th className="tw-border tw-py-2 tw-px-4">
                          Pipe Sec. No
                        </th>
                        <th className="tw-border tw-py-2 tw-px-4">
                          Pipe ID (in)
                        </th>
                        <th className="tw-border tw-py-2 tw-px-4">
                          SIPP Liner Length (ft)
                        </th>
                        <th className="tw-border tw-py-2 tw-px-4">
                          Resin Vol (ltrs)
                        </th>
                        <th className="tw-border tw-py-2 tw-px-4">Drums</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cartItems.map((cart) => {
                        let sectionCounter = 1;

                        return cart.items.map((item, i) => {
                          const sectionNo = sectionCounter++;
                          return (
                            <tr key={`${sectionNo}-${i}`} className="tw-border">
                              <td className="tw-border tw-py-2 tw-px-4">
                                {sectionNo}
                              </td>
                              <td className="tw-border tw-py-2 tw-px-4">
                                {item.internalDiameter || "N/A"}
                              </td>
                              <td className="tw-border tw-py-2 tw-px-4">
                                {item.lengthLined || "N/A"}
                              </td>
                              <td className="tw-border tw-py-2 tw-px-4">
                                {item.linerVol || "N/A"}
                              </td>
                              <td className="tw-border tw-py-2 tw-px-4">
                                {item.drumsResin || "N/A"}
                              </td>
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>

                  {/* Summary Section */}
                  <div className="tw-mt-6 tw-text-right tw-font-bold">
                    {/* <p>Subtotal: ${subtotal.toFixed(2)}</p>
            <p>Tax (10%): ${tax.toFixed(2)}</p> */}
                    <p className="tw-text-lg">
                      Total Cost:{" "}
                      <span className="tw-text-[#00B5ED]">
                        {" "}
                        ${parseFloat(totalCost).toLocaleString() || "0.00"}
                      </span>
                    </p>{" "}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="tw-grid tw-gap-4">
            {/* <div className="tw-grid tw-gap-2">
              <Label className="tw-font-medium" htmlFor="name">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
            </div>

            <div className="tw-grid tw-gap-2">
              <Label className="tw-font-medium" htmlFor="email">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
              />
            </div>

            <div className="tw-grid tw-gap-2">
              <Label className="tw-font-medium" htmlFor="address">
                Shipping Address
              </Label>
              <Input
                id="address"
                type="text"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter your shipping address"
                required
              />
            </div> */}
            {isLoading ? (
              <LoadingButton />
            ) : (
              <Button
                type="button"
                onClick={handlePlaceOrder}
                className="tw-w-full tw-font-semibold tw-mt-6 tw-text-sm tw-uppercase"
                disabled={cartItems.length === 0}
              >
                Place Order
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="tw-text-center tw-text-xs md:tw-text-sm tw-mb-6 tw-mt-2">
        Back to{" "}
        <Link to="/">
          <span className="tw-text-[#00B5ED] tw-font-medium">Home</span>
        </Link>
      </div>

      {/* Hidden Invoice for PDF Generation */}
      <div
        ref={invoiceRef}
        id="invoice-container"
        className="tw-absolute tw-top-[-9999px] tw-left-[-9999px] tw-w-[210mm] tw-h-[297mm] tw-flex tw-flex-col tw-bg-white tw-p-6 tw-shadow-lg tw-rounded-lg tw-font-sans tw-text-gray-700"
      >
        <div className="tw-flex-grow">
          {/* Header */}
          <div className="tw-flex tw-flex-col tw-justify-center tw-items-center">
            <h1 className="tw-text-2xl tw-uppercase tw-my-4 tw-font-bold tw-text-[#00B5ED] tw-tracking-widest">
              SIPP Americas
            </h1>
            <p className="tw-font-semibold tw-text-xl tw-tracking-wide">
              Invoice
            </p>
          </div>

          {/* <div className="tw-text-right">
              <p>
                <strong>Date:</strong> 11.02.2030
              </p>
              <p>
                <strong>Invoice No:</strong> 01234
              </p>
              <p>
                <strong>Due Date:</strong> 11.03.2030
              </p>
            </div> */}

          {/* Client Info */}
          {/* <div className="tw-mt-6">
            <p className="tw-text-lg tw-font-semibold">Issued To:</p>
            <p>Jonathan Patterson</p>
            <p>Liceria & Co.</p>
            <p>123 Anywhere St., Any City</p>
          </div> */}

          <div className="tw-my-6 tw-text-sm">
            <ul>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Project Title:
                </span>{" "}
                {projectDetails.title}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Project Location:
                </span>{" "}
                {projectDetails.location}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Client:
                </span>{" "}
                {projectDetails.clientName}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  SIPP Installer:
                </span>{" "}
                {projectDetails.sippInstaller}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  SIPP Calc Design Date:
                </span>{" "}
                {projectDetails.sippCalcDesignDate}
              </li>
            </ul>
          </div>

          {/* Table */}
          <table className="tw-w-full tw-mt-8 tw-border-collapse tw-border tw-text-left tw-flex-grow">
            <thead>
              <tr className="tw-bg-[#00B5ED] tw-text-white">
                <th className="tw-border tw-py-2 tw-px-4">Pipe Sec. No</th>
                <th className="tw-border tw-py-2 tw-px-4">Pipe ID (in)</th>
                <th className="tw-border tw-py-2 tw-px-4">
                  SIPP Liner Length (ft)
                </th>
                <th className="tw-border tw-py-2 tw-px-4">Resin Vol (ltrs)</th>
                <th className="tw-border tw-py-2 tw-px-4">Drums</th>
              </tr>
            </thead>
            <tbody>
              {cartItems.map((cart) => {
                let sectionCounter = 1;

                return cart.items.map((item, i) => {
                  const sectionNo = sectionCounter++;
                  return (
                    <tr key={`${sectionNo}-${i}`} className="tw-border">
                      <td className="tw-border tw-py-2 tw-px-4">{sectionNo}</td>
                      <td className="tw-border tw-py-2 tw-px-4">
                        {item.internalDiameter || "N/A"}
                      </td>
                      <td className="tw-border tw-py-2 tw-px-4">
                        {item.lengthLined || "N/A"}
                      </td>
                      <td className="tw-border tw-py-2 tw-px-4">
                        {item.linerVol || "N/A"}
                      </td>
                      <td className="tw-border tw-py-2 tw-px-4">
                        {item.drumsResin || "N/A"}
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>

          {/* Summary */}
          <div className="tw-mt-6 tw-text-right tw-font-bold">
            <p>
              Subtotal={" "}
              <span> ${parseFloat(totalCost).toLocaleString() || "0.00"}</span>
            </p>
            <p>Tax (0%)= 0.00</p>
            <p className="tw-text-lg">
              Total={" "}
              <span className="tw-text-[#00B5ED] tw-font-semibold">
                {" "}
                ${parseFloat(totalCost).toLocaleString() || "0.00"}
              </span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="tw-mt-auto tw-text-center tw-text-gray-500 tw-text-sm tw-font-bold">
          <p>THANK YOU</p>
          <p>SIPP Americas</p>
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </div>
  );
};

export default Checkout;
